import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PageLayout from "./layouts/PageLayout";
import Section from "./layouts/Section";
import blogData from "../data/blogData";

// Map blog slugs to related service pages for contextual internal linking
const blogToServiceMap = {
  "iphone-screen-repair-cost": ["iphone-repair", "screen-repair"],
  "samsung-screen-repair-cost": ["samsung-repair", "screen-repair"],
  "iphone-battery-replacement-cost": ["iphone-repair", "battery-replacement"],
  "water-damaged-phone-fix": ["water-damage-repair", "data-recovery"],
  "why-phone-wont-charge": ["charging-port-repair", "battery-replacement"],
  "repair-vs-replace-phone": ["iphone-repair", "samsung-repair"],
  "same-day-phone-repair-explained": ["screen-repair", "battery-replacement"],
  "top-phone-repair-shops-willow-grove": ["iphone-repair", "samsung-repair", "ipad-repair"],
  "best-phone-repair-willow-grove": ["iphone-repair", "samsung-repair"],
  "laptop-repair-cost-guide": ["data-recovery", "software-issues"],
  "best-screen-protector-guide": ["screen-repair"],
};

const serviceLabels = {
  "iphone-repair": { label: "iPhone Repair", icon: "fab fa-apple" },
  "samsung-repair": { label: "Samsung Repair", icon: "fab fa-android" },
  "ipad-repair": { label: "iPad Repair", icon: "fas fa-tablet-alt" },
  "screen-repair": { label: "Screen Repair", icon: "fas fa-mobile-alt" },
  "battery-replacement": { label: "Battery Replacement", icon: "fas fa-battery-full" },
  "charging-port-repair": { label: "Charging Port Repair", icon: "fas fa-plug" },
  "water-damage-repair": { label: "Water Damage Repair", icon: "fas fa-tint" },
  "data-recovery": { label: "Data Recovery", icon: "fas fa-database" },
  "software-issues": { label: "Software Issues", icon: "fas fa-cog" },
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogData[slug];
  const [openFaq, setOpenFaq] = useState(null);

  if (!post) {
    return (
      <PageLayout title="Article Not Found | Cell Station">
        <Section title="Not Found">
          <div className="section">
            <div className="container text-center" style={{ padding: "60px 0" }}>
              <h2>Article Not Found</h2>
              <p className="text-muted">The article you're looking for doesn't exist.</p>
              <Link to="/blog" className="btn btn-primary">
                Back to Blog
              </Link>
            </div>
          </div>
        </Section>
      </PageLayout>
    );
  }

  const canonical = `https://www.icellstation.com/blog/${post.slug}`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    image: `https://www.icellstation.com${post.heroImage}`,
    datePublished: post.publishDate,
    dateModified: post.publishDate,
    author: {
      "@type": "Organization",
      name: "Cell Station",
      url: "https://www.icellstation.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Cell Station",
      logo: {
        "@type": "ImageObject",
        url: "https://www.icellstation.com/assets/images/logo.jpg",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical,
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: post.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  // Related posts (exclude current)
  const relatedPosts = Object.values(blogData)
    .filter((p) => p.slug !== post.slug)
    .slice(0, 3);

  // Related service pages for contextual linking
  const relatedServices = (blogToServiceMap[post.slug] || []).map(
    (slug) => ({ slug, ...serviceLabels[slug] })
  );

  return (
    <PageLayout
      title={post.metaTitle}
      description={post.metaDescription}
      url={canonical}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <Section title={post.category}>
        <div className="section">
          <div className="container">
            {/* Breadcrumb */}
            <div className="mb-25">
              <Link to="/blog" style={{ color: "#F50963" }}>
                <i className="fas fa-arrow-left mr-2" />
                All Articles
              </Link>
            </div>

            <div className="row">
              <div className="col-lg-8">
                {/* Article Header */}
                <div style={{ marginBottom: "25px" }}>
                  <div
                    style={{
                      display: "inline-block",
                      background: "#F50963",
                      color: "#fff",
                      padding: "4px 14px",
                      borderRadius: "20px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      marginBottom: "15px",
                    }}
                  >
                    {post.category}
                  </div>
                  <h1 style={{ fontSize: "2.1rem", fontWeight: 700, color: "#18305d", lineHeight: 1.2 }}>
                    {post.title}
                  </h1>
                  <div style={{ color: "#888", fontSize: "0.9rem", marginTop: "12px" }}>
                    <i className="fas fa-user mr-2" />
                    {post.author}
                    <span style={{ margin: "0 10px" }}>·</span>
                    <i className="fas fa-calendar-alt mr-2" />
                    {post.publishDateDisplay}
                    <span style={{ margin: "0 10px" }}>·</span>
                    <i className="fas fa-clock mr-2" />
                    {post.readTime}
                  </div>
                </div>

                {/* Hero image */}
                {post.heroImage && (
                  <div style={{ marginBottom: "30px", borderRadius: "14px", overflow: "hidden" }}>
                    <img
                      src={process.env.PUBLIC_URL + post.heroImage}
                      alt={post.heroAlt}
                      loading="lazy"
                      style={{ width: "100%", height: "auto", display: "block" }}
                    />
                  </div>
                )}

                {/* Excerpt */}
                <p
                  style={{
                    fontSize: "1.15rem",
                    lineHeight: 1.6,
                    color: "#555",
                    fontStyle: "italic",
                    borderLeft: "3px solid #F50963",
                    paddingLeft: "16px",
                    marginBottom: "30px",
                  }}
                >
                  {post.excerpt}
                </p>

                {/* Body sections */}
                {post.sections.map((section, idx) => (
                  <div key={idx} style={{ marginBottom: "28px" }}>
                    <h2 style={{ fontSize: "1.45rem", fontWeight: 700, color: "#18305d", marginBottom: "14px" }}>
                      {section.heading}
                    </h2>
                    {section.paragraphs && section.paragraphs.map((p, pi) => (
                      <p key={pi} style={{ fontSize: "1rem", lineHeight: 1.75, color: "#444", marginBottom: "12px" }}>
                        {p}
                      </p>
                    ))}
                    {section.list && (
                      <ul className="check-list" style={{ marginLeft: "10px", marginTop: "12px" }}>
                        {section.list.map((li, li2) => (
                          <li key={li2} style={{ marginBottom: "8px", color: "#444" }}>
                            {li}
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.paragraphsAfter && section.paragraphsAfter.map((p, pi) => (
                      <p key={`after-${pi}`} style={{ fontSize: "1rem", lineHeight: 1.75, color: "#444", marginTop: "14px" }}>
                        {p}
                      </p>
                    ))}
                  </div>
                ))}

                {/* FAQs */}
                <div style={{ marginTop: "40px", marginBottom: "30px" }}>
                  <h2 style={{ fontSize: "1.45rem", fontWeight: 700, color: "#18305d", marginBottom: "18px" }}>
                    Frequently Asked Questions
                  </h2>
                  {post.faqs.map((faq, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                        marginBottom: "10px",
                        overflow: "hidden",
                      }}
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "16px 18px",
                          background: openFaq === idx ? "#f8f9fa" : "#fff",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontWeight: 600,
                          fontSize: "0.98rem",
                          color: "#333",
                        }}
                      >
                        {faq.question}
                        <i
                          className={`fas fa-chevron-${openFaq === idx ? "up" : "down"}`}
                          style={{ color: "#F50963", fontSize: "0.82rem" }}
                        />
                      </button>
                      {openFaq === idx && (
                        <div
                          style={{
                            padding: "14px 18px 18px",
                            color: "#555",
                            lineHeight: 1.7,
                            borderTop: "1px solid #e9ecef",
                            background: "#fafbfc",
                          }}
                        >
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* CTA card */}
                <div
                  style={{
                    marginTop: "40px",
                    padding: "30px",
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, #18305d 0%, #0f1d3a 100%)",
                    color: "#fff",
                    textAlign: "center",
                  }}
                >
                  <h3 style={{ color: "#fff", marginBottom: "10px" }}>
                    Need a Repair? We Can Help.
                  </h3>
                  <p style={{ opacity: 0.85, marginBottom: "20px" }}>
                    Same-day service. 30-day warranty. 4.6★ rated by 163+ customers.
                  </p>
                  <Link
                    to="/contact-us"
                    className="btn btn-primary"
                    style={{ padding: "12px 28px", marginRight: "10px" }}
                  >
                    <i className="fas fa-bolt mr-2" />
                    Fix My Phone Now
                  </Link>
                  <a
                    href="tel:+12159393933"
                    className="btn"
                    style={{ padding: "12px 28px", background: "#ffaa19", color: "#18305d" }}
                  >
                    <i className="fas fa-phone-alt mr-2" />
                    (215) 939-3933
                  </a>
                </div>
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                <div
                  style={{
                    background: "#f8f9fa",
                    padding: "25px",
                    borderRadius: "12px",
                    marginBottom: "25px",
                    position: "sticky",
                    top: "20px",
                  }}
                >
                  <h4 className="text-dark mb-15">Get a Free Quote</h4>
                  <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "15px" }}>
                    Not sure what you need? We'll diagnose for free.
                  </p>
                  <Link to="/contact-us" className="btn btn-primary w-100 mb-10" style={{ padding: "12px" }}>
                    Request Quote
                  </Link>
                  <a
                    href="tel:+12159393933"
                    className="btn w-100"
                    style={{ padding: "12px", background: "#18305d", color: "#fff", borderRadius: "5px" }}
                  >
                    <i className="fas fa-phone-alt mr-2" />
                    Call Now
                  </a>
                </div>

                {/* Related services — contextual internal links */}
                {relatedServices.length > 0 && (
                  <div
                    style={{
                      background: "#ffffff",
                      padding: "25px",
                      borderRadius: "12px",
                      border: "1px solid #eee",
                      marginBottom: "25px",
                    }}
                  >
                    <h4 className="text-dark mb-15">Related Services</h4>
                    <ul className="list-unstyled">
                      {relatedServices.map((s) => (
                        <li key={s.slug} style={{ marginBottom: "10px" }}>
                          <Link
                            to={`/services/${s.slug}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "#4a4a4a",
                              padding: "10px 12px",
                              background: "#f8f9fa",
                              borderRadius: "8px",
                              fontWeight: 500,
                              fontSize: "0.95rem",
                            }}
                          >
                            <i
                              className={s.icon}
                              style={{ color: "#F50963", width: "24px", fontSize: "1rem" }}
                            />
                            <span style={{ marginLeft: "8px" }}>{s.label}</span>
                            <i
                              className="fas fa-arrow-right"
                              style={{
                                marginLeft: "auto",
                                fontSize: "0.75rem",
                                color: "#ccc",
                              }}
                            />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Related articles */}
                <div style={{ background: "#ffffff", padding: "25px", borderRadius: "12px", border: "1px solid #eee" }}>
                  <h4 className="text-dark mb-15">Related Articles</h4>
                  <ul className="list-unstyled">
                    {relatedPosts.map((p) => (
                      <li key={p.slug} style={{ marginBottom: "14px", paddingBottom: "14px", borderBottom: "1px solid #f0f0f0" }}>
                        <Link to={`/blog/${p.slug}`} style={{ color: "#18305d", fontWeight: 600, fontSize: "0.95rem", lineHeight: 1.4, display: "block", marginBottom: "4px" }}>
                          {p.title}
                        </Link>
                        <span style={{ fontSize: "0.8rem", color: "#888" }}>
                          {p.category} · {p.readTime}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default BlogPost;
